import { createColumnHelper } from '@tanstack/table-core';
import { StickyOptions } from 'common/components/table/tableTypes';
import {
  AdvancedReportTableCell,
  AdvancedReportTableRow
} from './advanced-reports-table.types';
import Blur from 'features/blur/blur';
import MarketingSourceLogo from 'common/components/icons/marketingSourceLogo';
import { CurrencyDisplayWrapper } from 'features/currency/currencyDisplayWrapper';
import Spinner, { SpinnerSize } from 'common/components/loading/spinner';
import AdvancedReportsTableAccordion from './advanced-reports-table-controls/advanced-reports-table-accordion';
import { MetricTypes } from 'common/constants/metric-types';
import { AdvancedReportQueryStatus } from '../advanced-reports.types';
import { QueryStatus } from '@reduxjs/toolkit/dist/query';

function setDashAsDefaultAfterFormat(value: string): string {
  if (!value || value === '0') {
    return '-';
  }

  return value;
}

const reportColumnHelper = createColumnHelper<AdvancedReportTableRow>();

function createHeader(headerName: string, isLoading: boolean) {
  return (
    <div className="flex items-center">
      <span className="relative">
        {headerName}
        {isLoading ? (
          <div className="absolute -right-5 top-1/2 -translate-y-1/2 bg-white">
            <Spinner size={SpinnerSize.SMALL} />
          </div>
        ) : (
          <></>
        )}
      </span>
    </div>
  );
}

export const fieldColumnId = 'Field';

export const getAdvancedReportColumnDefs = (
  currencyCode: string,
  primaryQueryStatus: AdvancedReportQueryStatus,
  compareQueryStatus: AdvancedReportQueryStatus,
  displayComparisonAttributionModelData: boolean
) => {
  const primaryImpressionsIsLoading =
    primaryQueryStatus.impressions === QueryStatus.pending;
  const primaryClicksIsLoading =
    primaryQueryStatus.clicks === QueryStatus.pending;
  const primaryCallsIsLoading =
    primaryQueryStatus.calls === QueryStatus.pending;
  const primaryWebsIsLoading = primaryQueryStatus.webs === QueryStatus.pending;
  const primaryOppsIsLoading = primaryQueryStatus.opps === QueryStatus.pending;
  const primaryOppValuesIsLoading =
    primaryQueryStatus.oppValues === QueryStatus.pending;
  const primarySalesIsLoading =
    primaryQueryStatus.sales === QueryStatus.pending;
  const primaryRevenuesIsLoading =
    primaryQueryStatus.revenues === QueryStatus.pending;
  const primaryCostsIsLoading =
    primaryQueryStatus.costs === QueryStatus.pending;

  const compareImpressionsIsLoading =
    compareQueryStatus.impressions === QueryStatus.pending;
  const compareClicksIsLoading =
    compareQueryStatus.clicks === QueryStatus.pending;
  const compareCallsIsLoading =
    compareQueryStatus.calls === QueryStatus.pending;
  const compareWebsIsLoading = compareQueryStatus.webs === QueryStatus.pending;
  const compareOppsIsLoading = compareQueryStatus.opps === QueryStatus.pending;
  const compareOppValuesIsLoading =
    compareQueryStatus.oppValues === QueryStatus.pending;
  const compareSalesIsLoading =
    compareQueryStatus.sales === QueryStatus.pending;
  const compareRevenuesIsLoading =
    compareQueryStatus.revenues === QueryStatus.pending;
  const compareCostsIsLoading =
    compareQueryStatus.costs === QueryStatus.pending;

  return [
    reportColumnHelper.accessor((row) => row.field, {
      id: fieldColumnId,
      header: () => <span>Field</span>,
      enableColumnFilter: false,
      cell: (info) =>
        createFieldCell(
          info.getValue(),
          info.row.original.id,
          info.row.original.hasChildren,
          info.row.original.shouldBlurField
        ),
      meta: {
        stickyOptions: {
          isEnabled: true,
          side: 'left'
        } as StickyOptions
      },
      enableGlobalFilter: false,
      enableSorting: false,
      minSize: 200
    }),
    reportColumnHelper.accessor((row) => row.impressions, {
      id: MetricTypes.IMPRESSIONS,
      header: () =>
        createHeader(
          'Impressions',
          primaryImpressionsIsLoading || compareImpressionsIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createImpressionsCell(
          info.getValue(),
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.clicks, {
      id: MetricTypes.CLICKS,
      header: () =>
        createHeader(
          'Clicks',
          primaryClicksIsLoading || compareClicksIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createClicksCell(
          info.getValue(),
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.call, {
      id: MetricTypes.INBOUND_CALL,
      header: () =>
        createHeader('Calls', primaryCallsIsLoading || compareCallsIsLoading),
      enableColumnFilter: false,
      cell: (info) =>
        createCallCell(info.getValue(), displayComparisonAttributionModelData),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.web, {
      id: MetricTypes.WEB,
      header: () =>
        createHeader('Forms', primaryWebsIsLoading || compareWebsIsLoading),
      enableColumnFilter: false,
      cell: (info) =>
        createWebCell(info.getValue(), displayComparisonAttributionModelData),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.opportunity, {
      id: MetricTypes.OPPORTUNITIES,
      header: () =>
        createHeader('Opps', primaryOppsIsLoading || compareOppsIsLoading),
      enableColumnFilter: false,
      cell: (info) =>
        createOpportunityCell(
          info.getValue(),
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.opportunityValue, {
      id: MetricTypes.OPPORTUNITY_VALUES,
      header: () =>
        createHeader(
          'Opps Value',
          primaryOppValuesIsLoading || compareOppValuesIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createOpportunityValueCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.sales, {
      id: MetricTypes.REVENUES,
      header: () =>
        createHeader('Sales', primarySalesIsLoading || compareSalesIsLoading),
      enableColumnFilter: false,
      cell: (info) =>
        createSaleCell(info.getValue(), displayComparisonAttributionModelData),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.revenue, {
      id: MetricTypes.REVENUE_VALUES,
      header: () =>
        createHeader(
          'Revenue',
          primaryRevenuesIsLoading || compareRevenuesIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createRevenueCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.cost, {
      id: MetricTypes.COST,
      header: () =>
        createHeader('Cost', primaryCostsIsLoading || compareCostsIsLoading),
      enableColumnFilter: false,
      cell: (info) =>
        createCostCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.cpa, {
      id: MetricTypes.CPA,
      header: () =>
        createHeader(
          'CPA',
          primaryCostsIsLoading ||
            primaryWebsIsLoading ||
            primaryCallsIsLoading ||
            compareCostsIsLoading ||
            compareWebsIsLoading ||
            compareCallsIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createCpaCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.cps, {
      id: MetricTypes.CPS,
      header: () =>
        createHeader(
          'CPS',
          primaryCostsIsLoading ||
            primarySalesIsLoading ||
            compareCostsIsLoading ||
            compareSalesIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createCpsCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.roi, {
      id: MetricTypes.ROI,
      header: () =>
        createHeader(
          'ROI',
          primaryCostsIsLoading ||
            primaryRevenuesIsLoading ||
            compareCostsIsLoading ||
            compareRevenuesIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createRoiCell(
          info.getValue(),
          currencyCode,
          displayComparisonAttributionModelData
        ),
      size: 100
    }),
    reportColumnHelper.accessor((row) => row.roas, {
      id: MetricTypes.ROAS,
      header: () =>
        createHeader(
          'ROAS',
          primaryCostsIsLoading ||
            primaryRevenuesIsLoading ||
            compareCostsIsLoading ||
            compareRevenuesIsLoading
        ),
      enableColumnFilter: false,
      cell: (info) =>
        createRoasCell(info.getValue(), displayComparisonAttributionModelData),
      size: 100
    })
  ].filter((column) => column !== undefined) as any[];
};

function getFieldCell(cellContent: string) {
  return (
    <div className="flex items-center">
      <MarketingSourceLogo
        marketingSource={cellContent}
        iconClasses="text-sm flex mr-2"
        imageClasses="h-3 mr-2"
      />
      <span className="text-sm overflow-hidden block">{cellContent}</span>
    </div>
  );
}
export const createFieldCell = (
  value: string,
  rowId: string,
  rowHasChildren: boolean,
  shouldBlurFieldColumn: boolean
) => (
  <div className="relative flex items-center">
    <div className="flex mx-2 w-4 h-4">
      <AdvancedReportsTableAccordion rowId={rowId} isHidden={!rowHasChildren} />
    </div>

    {shouldBlurFieldColumn ? (
      <Blur>{getFieldCell(value)}</Blur>
    ) : (
      getFieldCell(value)
    )}
  </div>
);

export const createImpressionsCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {setDashAsDefaultAfterFormat(
        Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.primaryValue)
      )}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {setDashAsDefaultAfterFormat(
          Intl.NumberFormat('en-GB', {
            maximumFractionDigits: 2
          }).format(cell.compareValue)
        )}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createClicksCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createCallCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createWebCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createOpportunityCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createOpportunityValueCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createSaleCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm overflow-hidden block">
      {Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs overflow-hidden block">
        {Intl.NumberFormat('en-GB', {
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createRevenueCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createCostCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createCpaCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createCpsCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createRoiCell = (
  cell: AdvancedReportTableCell,
  currencyCode: string,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      <CurrencyDisplayWrapper currencyCode={currencyCode}>
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.primaryValue)}
      </CurrencyDisplayWrapper>
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        <CurrencyDisplayWrapper currencyCode={currencyCode}>
          {Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }).format(cell.compareValue)}
        </CurrencyDisplayWrapper>
      </div>
    ) : (
      <></>
    )}
  </div>
);

export const createRoasCell = (
  cell: AdvancedReportTableCell,
  displayComparisonAttributionModelData: boolean
) => (
  <div>
    <div className="text-sm">
      {Intl.NumberFormat('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(cell.primaryValue)}
    </div>
    {displayComparisonAttributionModelData ? (
      <div className="text-xs">
        {Intl.NumberFormat('en-GB', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(cell.compareValue)}
      </div>
    ) : (
      <></>
    )}
  </div>
);
